import { ApiError } from '@/lib/api-client';

export interface FriendlyError {
  title: string;
  message: string;
  isRetryable: boolean;
}

const KNOWN_MESSAGES: Array<{
  pattern: string;
  title: string;
  message: string;
  isRetryable: boolean;
}> = [
  {
    pattern: 'Project not found',
    title: 'Not found',
    message: 'This project could not be found. It may have been deleted.',
    isRetryable: false,
  },
  {
    pattern: 'generation is already in progress',
    title: 'Generation in progress',
    message: 'A generation is already running. Please wait for it to finish.',
    isRetryable: false,
  },
  {
    pattern: 'Genre must be set',
    title: 'Missing settings',
    message: 'Please select a music genre before generating.',
    isRetryable: false,
  },
  {
    pattern: 'Title is required',
    title: 'Missing field',
    message: 'Please provide a title for your project.',
    isRetryable: false,
  },
];

const STATUS_MAP: Record<number, FriendlyError> = {
  401: {
    title: 'Session expired',
    message: 'Your session has expired. Please sign in again.',
    isRetryable: false,
  },
  403: {
    title: 'Access denied',
    message: 'You do not have permission to perform this action.',
    isRetryable: false,
  },
  404: {
    title: 'Not found',
    message: 'The requested resource could not be found.',
    isRetryable: false,
  },
  429: {
    title: 'Too many requests',
    message: 'You are making requests too quickly. Please wait a moment and try again.',
    isRetryable: true,
  },
};

const NETWORK_ERROR_PATTERNS = ['Failed to fetch', 'NetworkError', 'Load failed'];

export function isNetworkError(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    !(error instanceof ApiError) &&
    NETWORK_ERROR_PATTERNS.some((p) => error.message.includes(p))
  );
}

export function toFriendlyError(error: unknown): FriendlyError {
  if (isNetworkError(error)) {
    return {
      title: 'Connection error',
      message: 'Unable to reach the server. Please check your internet connection and try again.',
      isRetryable: true,
    };
  }

  if (error instanceof ApiError) {
    // Check known message patterns first
    for (const known of KNOWN_MESSAGES) {
      if (error.message.includes(known.pattern)) {
        return { title: known.title, message: known.message, isRetryable: known.isRetryable };
      }
    }

    // Fall back to status code
    const statusEntry = STATUS_MAP[error.status];
    if (statusEntry) {
      return statusEntry;
    }

    // 5xx server errors
    if (error.status >= 500) {
      return {
        title: 'Server error',
        message: 'Something went wrong on our end. Please try again later.',
        isRetryable: true,
      };
    }
  }

  return {
    title: 'Something went wrong',
    message: 'An unexpected error occurred. Please try again.',
    isRetryable: false,
  };
}
